import type {
  CreateRehearsalSessionDTO,
  RehearsalSessionDTO,
} from "../contracts/rehearsal-sessions";
import { formatRehearsalDate, summarizeRehearsalDayStatus } from "./rehearsal-sessions-ui";

export type RehearsalMatrixPerformer = {
  id: string;
  name: string;
};

export type RehearsalMatrixCellNone = { kind: "none" };

export type RehearsalMatrixCellSession = {
  kind: "session";
  session: RehearsalSessionDTO;
};

export type RehearsalMatrixCell =
  | RehearsalMatrixCellNone
  | RehearsalMatrixCellSession;

export type RehearsalMatrixDateColumn = {
  scheduledDate: string;
  label: string;
  statusCounts: ReturnType<typeof summarizeRehearsalDayStatus>;
};

export type RehearsalMatrixRow = {
  performerId: string;
  performerName: string;
  cells: RehearsalMatrixCell[];
};

export type RehearsalScheduleMatrixModel = {
  dates: RehearsalMatrixDateColumn[];
  rows: RehearsalMatrixRow[];
};

export function rehearsalMatrixSessionKey(
  performerId: string,
  scheduledDate: string,
): string {
  return `${performerId}:${scheduledDate}`;
}

export function buildRehearsalScheduleMatrix(
  performers: RehearsalMatrixPerformer[],
  sessions: RehearsalSessionDTO[],
): RehearsalScheduleMatrixModel {
  const sessionsByKey = new Map<string, RehearsalSessionDTO>();
  const dateSet = new Set<string>();

  for (const session of sessions) {
    dateSet.add(session.scheduledDate);
    sessionsByKey.set(
      rehearsalMatrixSessionKey(session.performerId, session.scheduledDate),
      session,
    );
  }

  const sortedDates = [...dateSet].sort((a, b) => a.localeCompare(b));
  const sessionsByDate = new Map<string, RehearsalSessionDTO[]>();
  for (const session of sessions) {
    const bucket = sessionsByDate.get(session.scheduledDate) ?? [];
    bucket.push(session);
    sessionsByDate.set(session.scheduledDate, bucket);
  }

  const dates: RehearsalMatrixDateColumn[] = sortedDates.map((scheduledDate) => ({
    scheduledDate,
    label: formatRehearsalDate(scheduledDate),
    statusCounts: summarizeRehearsalDayStatus(
      sessionsByDate.get(scheduledDate) ?? [],
    ),
  }));

  const rows: RehearsalMatrixRow[] = performers.map((performer) => ({
    performerId: performer.id,
    performerName: performer.name,
    cells: sortedDates.map((scheduledDate) => {
      const session = sessionsByKey.get(
        rehearsalMatrixSessionKey(performer.id, scheduledDate),
      );
      if (!session) return { kind: "none" as const };
      return { kind: "session" as const, session };
    }),
  }));

  return { dates, rows };
}

export function rehearsalMatrixToggleTargetStatus(
  session: RehearsalSessionDTO,
): RehearsalSessionDTO["status"] | null {
  if (session.status === "planned") return "completed";
  if (session.status === "completed") return "planned";
  return null;
}

export function rehearsalMatrixCreateSessionInput(
  performerId: string,
  scheduledDate: string,
): CreateRehearsalSessionDTO {
  return {
    performerId,
    scheduledDate,
    status: "planned",
    location: null,
    notes: null,
  };
}

export type RehearsalMatrixBulkAddPayload = {
  performerIds: string[];
  scheduledDates: string[];
};

function rehearsalMatrixSessionsFromCells(
  cells: RehearsalMatrixCell[],
  status?: RehearsalSessionDTO["status"],
): RehearsalSessionDTO[] {
  return cells
    .filter((cell): cell is RehearsalMatrixCellSession => cell.kind === "session")
    .map((cell) => cell.session)
    .filter((session) => status == null || session.status === status);
}

function rehearsalMatrixDateIndex(
  model: RehearsalScheduleMatrixModel,
  scheduledDate: string,
): number {
  return model.dates.findIndex((date) => date.scheduledDate === scheduledDate);
}

export function rehearsalMatrixPlannedSessionIdsForDate(
  model: RehearsalScheduleMatrixModel,
  scheduledDate: string,
): string[] {
  const dateIndex = rehearsalMatrixDateIndex(model, scheduledDate);
  if (dateIndex < 0) return [];

  return model.rows.flatMap((row) => {
    const cell = row.cells[dateIndex];
    if (cell?.kind !== "session" || cell.session.status !== "planned") {
      return [];
    }
    return [cell.session.id];
  });
}

export function rehearsalMatrixPlannedSessionIdsForPerformer(
  model: RehearsalScheduleMatrixModel,
  performerId: string,
): string[] {
  const row = model.rows.find((entry) => entry.performerId === performerId);
  if (!row) return [];

  return rehearsalMatrixSessionsFromCells(row.cells, "planned").map(
    (session) => session.id,
  );
}

export function rehearsalMatrixBulkAddToPlanForDate(
  model: RehearsalScheduleMatrixModel,
  scheduledDate: string,
): RehearsalMatrixBulkAddPayload | null {
  const dateIndex = rehearsalMatrixDateIndex(model, scheduledDate);
  if (dateIndex < 0) return null;

  const performerIds = model.rows
    .filter((row) => row.cells[dateIndex]?.kind === "none")
    .map((row) => row.performerId);

  if (performerIds.length === 0) return null;

  return { performerIds, scheduledDates: [scheduledDate] };
}

export function rehearsalMatrixBulkAddToPlanForPerformer(
  model: RehearsalScheduleMatrixModel,
  performerId: string,
): RehearsalMatrixBulkAddPayload | null {
  const row = model.rows.find((entry) => entry.performerId === performerId);
  if (!row) return null;

  const scheduledDates = row.cells.flatMap((cell, index) => {
    if (cell.kind !== "none") return [];
    const scheduledDate = model.dates[index]?.scheduledDate;
    return scheduledDate ? [scheduledDate] : [];
  });

  if (scheduledDates.length === 0) return null;

  return { performerIds: [performerId], scheduledDates };
}

export function rehearsalMatrixSelectableSessionIds(
  model: RehearsalScheduleMatrixModel,
): string[] {
  return model.rows.flatMap((row) =>
    row.cells.flatMap((cell) => {
      if (cell.kind !== "session") return [];
      if (cell.session.status === "cancelled") return [];
      return [cell.session.id];
    }),
  );
}
